import { useCallback, useState } from 'react';
import { StanButton, StanCard, StanDropdownMenu, StanFilters, StanTable } from '@/components';
import { createIconElement, getLocalTimezone, stanNotify } from '@/lib';
import { useAffiliates } from '../../queries/affiliateAnalyticsQueries';
import { useAffiliateActions } from '../../queries/useAffiliateActions';
import { useQueryFilters } from '@/hooks/useQueryFilters';
import { AffiliateReseller } from '../../types/affiliateAnalytics.types';
import { AFFILIATE_FILTERS } from '../../constants/analyticsConstants';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { LuEllipsisVertical } from 'react-icons/lu';
import IcPlus from '@/assets/icons/ic-plus.svg';
import { useAffiliateCSVExport } from '../../hooks/useAffiliateCSVExport';
import { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { useModal } from '@/hooks/useModal';
import { AddAffiliateModal } from './AddAffiliateModal';
import { RevokeAccessModal } from './RevokeAccessModal';
import { EditCommissionModal } from './EditCommissionModal';
import { AffiliateEmailModal } from './AffiliateEmailModal';
import { useAffiliateTableColumns } from '../../hooks/useAffiliateTableColumns';

/**
 * AffiliateTableSection - Isolated component that manages table, filters, pagination, and modal state.
 * Fetches its own data independently to prevent re-rendering metrics on filter/pagination changes.
 */
export function AffiliateTableSection({ pageId }: { pageId: string }) {
    const [page, setPage] = useState(1);
    const [selectedReseller, setSelectedReseller] = useState<AffiliateReseller | null>(null);

    // Filter configuration and state management
    const { filterValues, backendFilterValues, handleFilterChange, handleResetFilters } = useQueryFilters({
        filters: AFFILIATE_FILTERS,
    });
    const { exportToCSV, isExporting } = useAffiliateCSVExport(pageId, backendFilterValues);

    const { isOpen: isAddAffiliateModalOpen, open: openAddAffiliateModal, close: closeAddAffiliateModal } = useModal();
    const {
        isOpen: isEditAffiliateCommissionModalOpen,
        open: openEditAffiliateCommissionModal,
        close: closeEditAffiliateCommissionModal,
    } = useModal();
    const {
        isOpen: isRevokeAffiliateModalOpen,
        open: openRevokeAffiliateModal,
        close: closeRevokeAffiliateModal,
    } = useModal();
    const { isOpen: isSendEmailModalOpen, open: openSendEmailModal, close: closeSendEmailModal } = useModal();

    // Actions (mutations)
    const { resendLink } = useAffiliateActions(pageId);

    // Fetch affiliates data with current filters and pagination
    const { data: affiliatesData, isFetching: isAffiliatesLoading } = useAffiliates(pageId, {
        page,
        filter_array: backendFilterValues,
        timezone: getLocalTimezone(),
    });

    const handleSendEmail = useCallback(() => {
        const totalAffiliates = affiliatesData?.metadata?.totalCustomers ?? 0;
        if (totalAffiliates > 0) {
            openSendEmailModal();
        }
    }, [openSendEmailModal, affiliatesData]);

    // Computed values
    const computedEmptyHeader = filterValues.length > 0 ? 'No Affiliates Match Filters' : 'No Affiliates Yet';
    const totalAffiliates = affiliatesData?.metadata?.totalCustomers ?? 0;
    const hasAffiliates = totalAffiliates > 0;

    // Action options - disable Send Email if no affiliates
    const actionOptions: DropdownMenuOption[] = [
        { label: 'Send Email', value: 'send-email', disabled: !hasAffiliates },
        { label: 'Download CSV', value: 'download-csv' },
    ];

    const columns = useAffiliateTableColumns({
        onRevokeAccess: (reseller: AffiliateReseller) => {
            setSelectedReseller(reseller);
            openRevokeAffiliateModal();
        },
        onResendLink: (reseller: AffiliateReseller) => {
            resendLink.mutate(reseller.id, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Send Link',
                        text: 'Affiliate Link has been sent!',
                        duration: 2000,
                    });
                },
                onError: error => {
                    const errorMessage =
                        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                        'Failed to resend link';
                    stanNotify({
                        type: 'error',
                        title: 'Unable to Resend Affiliate Link',
                        text: errorMessage,
                    });
                },
            });
        },
        onEditCommission: (reseller: AffiliateReseller) => {
            setSelectedReseller(reseller);
            openEditAffiliateCommissionModal();
        },
    });

    return (
        <StanCard>
            <div className='mb-4 flex w-full items-start justify-between gap-4 max-md:hidden'>
                <StanFilters
                    filters={AFFILIATE_FILTERS}
                    onFilterChanged={handleFilterChange}
                    onResetFilters={handleResetFilters}
                    filterValues={filterValues}
                    className='max-w-[90%]'
                />
                <div className='flex items-center gap-4'>
                    <StanButton
                        name='add-affiliate-button'
                        size='sm'
                        onClick={openAddAffiliateModal}
                        icon={createIconElement(IcPlus, { className: 'size-5' })}
                        className='!my-0 px-3'
                        disabled={isExporting}
                    >
                        Add Affiliate
                    </StanButton>
                    <DropdownProvider dropdownId='affiliate-actions-dropdown'>
                        <StanDropdownMenu
                            id='affiliate-actions-dropdown'
                            name='affiliate-actions-dropdown'
                            options={actionOptions}
                            onSelect={(_, option: DropdownMenuOption) => {
                                if (option.value === 'send-email') {
                                    handleSendEmail();
                                } else if (option.value === 'download-csv') {
                                    exportToCSV();
                                }
                            }}
                            placement='bottom-end'
                            minWidth={230}
                            rounded
                        >
                            <LuEllipsisVertical className='text-gray size-6' />
                        </StanDropdownMenu>
                    </DropdownProvider>
                </div>
            </div>

            <StanTable
                columns={columns}
                data={affiliatesData?.data ?? []}
                isLoading={isAffiliatesLoading}
                page={page}
                totalPages={affiliatesData?.metadata?.totalPages ?? 0}
                onPageChange={setPage}
                emptyHeader={computedEmptyHeader}
                emptySubheader="Update filters to find what you're looking for!"
                className='affiliate-analytics-table w-full'
            />

            <AddAffiliateModal pageId={pageId} isOpen={isAddAffiliateModalOpen} onClose={closeAddAffiliateModal} />
            <EditCommissionModal
                isOpen={isEditAffiliateCommissionModalOpen}
                onClose={() => {
                    closeEditAffiliateCommissionModal();
                    setSelectedReseller(null);
                }}
                reseller={selectedReseller}
                pageId={pageId}
            />
            <RevokeAccessModal
                isOpen={isRevokeAffiliateModalOpen}
                onClose={() => {
                    closeRevokeAffiliateModal();
                    setSelectedReseller(null);
                }}
                reseller={selectedReseller}
                pageId={pageId}
            />
            <AffiliateEmailModal
                isOpen={isSendEmailModalOpen}
                onClose={closeSendEmailModal}
                pageId={pageId}
                totalAffiliates={affiliatesData?.metadata?.totalCustomers ?? 0}
                filterArray={backendFilterValues ?? ''}
            />
        </StanCard>
    );
}
